import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, LayoutDashboard, Rocket, FileText, Loader2, Eye, EyeOff } from "lucide-react";
import { cpost, cget, clientHttp } from "../lib/clientApi";
import { peso } from "../lib/pricing";
import { Seo } from "../lib/seo";

const APP_URL = "https://app.connect.bybloomlabs.com/v2/";

export default function Welcome() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const st = loc.state || {};
  const token = st.token || params.get("token") || "";
  const orderId = st.orderId || params.get("order") || "";
  const email = st.email || "";
  const summary = st.summary || null;

  const [stage, setStage] = useState("splash"); // splash | account | choices
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);
  const [needsPw, setNeedsPw] = useState(false);
  const authTried = useRef(false);

  // Sign the buyer in via the one-time onboarding token (sets the client cookie).
  useEffect(() => {
    if (authTried.current) return;
    authTried.current = true;
    if (token) {
      cpost("/client/auth/verify-magic-link", { token })
        .then((r) => { localStorage.setItem("bc_client_session", "1"); setMe(r?.user || null); setAuthed(true); setNeedsPw(!(r?.user?.has_password)); })
        .catch(() => setAuthed(false));
    } else {
      // No token — if the visitor is already signed in but hasn't set a password,
      // resume the onboarding (password) step instead of skipping it.
      cget("/client/me")
        .then((u) => { setMe(u); setAuthed(true); setNeedsPw(!u?.has_password); })
        .catch(() => setAuthed(false));
    }
  }, [token]);

  // Apple-esque splash auto-advances.
  useEffect(() => {
    if (stage !== "splash") return;
    const t = setTimeout(() => setStage((token || needsPw) ? "account" : "choices"), 2800);
    return () => clearTimeout(t);
  }, [stage, token, needsPw]);

  return (
    <div className="min-h-screen bg-[#04140E] text-white relative overflow-hidden" data-testid="welcome-page">
      <Seo title="Welcome" path="/welcome" description="Welcome to the Bloom Ecosystem." noindex />
      <style>{`
        @keyframes bloomFadeUp { 0% { opacity:0; transform: translateY(18px) scale(.98); } 100% { opacity:1; transform:none; } }
        @keyframes bloomGlow { 0%,100% { opacity:.35; } 50% { opacity:.7; } }
        .bloom-fade { animation: bloomFadeUp .9s cubic-bezier(.16,1,.3,1) both; }
        .bloom-delay-1 { animation-delay:.15s; } .bloom-delay-2 { animation-delay:.3s; } .bloom-delay-3 { animation-delay:.45s; }
      `}</style>
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-[#0A5C41] blur-[120px]" style={{ animation: "bloomGlow 6s ease-in-out infinite" }} />
      <div className="relative z-10 min-h-screen grid place-items-center px-6 py-16">
        {stage === "splash" && <Splash />}
        {stage === "account" && (
          <AccountStage authed={authed} onDone={() => setStage("choices")} onSkip={() => setStage("choices")} />
        )}
        {stage === "choices" && (
          <Choices nav={nav} orderId={orderId} email={email} summary={summary} me={me} />
        )}
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div className="text-center max-w-2xl" data-testid="welcome-splash">
      <div className="bloom-fade inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-500" /> Bloom Ecosystem
      </div>
      <h1 className="bloom-fade bloom-delay-1 mt-8 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white">
        Welcome to the<br />Bloom Ecosystem
      </h1>
      <p className="bloom-fade bloom-delay-2 mt-6 text-base sm:text-lg text-white/60">
        Your payment is confirmed. We're setting things up…
      </p>
      <div className="bloom-fade bloom-delay-3 mt-10 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
      </div>
    </div>
  );
}

function AccountStage({ authed, onDone, onSkip }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await cpost("/client/auth/set-password", { password: pw });
      setDone(true);
      setTimeout(onDone, 800);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't set your password. You can do this later from the dashboard.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bloom-fade w-full max-w-md" data-testid="welcome-account">
      <div className="text-center">
        <h2 className="text-3xl font-semibold tracking-tight text-white">Create your account</h2>
        <p className="mt-3 text-sm text-white/60">Set a password so you can sign back into your dashboard anytime.</p>
      </div>
      {!authed && (
        <p className="mt-4 text-center text-xs text-signal" data-testid="welcome-account-warn">
          We couldn't auto-verify this session. You can still set your password later from the dashboard.
        </p>
      )}
      {done ? (
        <div className="mt-8 flex items-center justify-center gap-2 text-brand-500" data-testid="welcome-account-done">
          <CheckCircle2 className="h-5 w-5" /> Password set
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="relative">
            <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password"
              className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 pr-11 text-white placeholder-white/40 outline-none focus:border-brand-500/60"
              data-testid="welcome-password" autoFocus />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 disabled:opacity-40" data-testid="welcome-password-toggle">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <input type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm password"
            className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-white/40 outline-none focus:border-brand-500/60"
            data-testid="welcome-password-confirm" />
          {err && <p className="text-negative text-sm" data-testid="welcome-account-error">{err}</p>}
          <button type="submit" disabled={busy}
            className="w-full rounded-xl bg-brand-500 hover:bg-brand-600 transition-colors py-3 font-medium text-white disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="welcome-set-password-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish creating account <ArrowRight className="h-4 w-4" /></>}
          </button>
          <button type="button" onClick={onSkip} className="w-full text-center text-sm text-white/50 hover:text-white/80" data-testid="welcome-skip-btn">
            Skip for now
          </button>
        </form>
      )}
    </div>
  );
}

function Choices({ nav, orderId, email, summary, me }) {
  const [dl, setDl] = useState("");
  const [launching, setLaunching] = useState(false);
  const [credits, setCredits] = useState(summary?.credits ?? null);
  const [paid, setPaid] = useState(summary?.total ?? summary?.amount ?? null);
  useEffect(() => {
    if (credits != null && paid != null) return;
    cget("/client/orders").then((r) => {
      const orders = r?.orders || [];
      const o = orderId ? orders.find((x) => x.id === orderId) : orders[0];
      if (o && paid == null && typeof o.amount_php === "number") setPaid(o.amount_php);
    }).catch(() => {});
    if (credits == null) {
      cget("/client/subscriptions").then((r) => {
        const bal = (r?.subscriptions || []).map((s) => s.credit_balance).find((v) => typeof v === "number");
        if (typeof bal === "number") setCredits(bal);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line
  const firstName = ((me?.name || "").trim().split(" ")[0]) || "";
  const launchApp = async () => {
    setLaunching(true);
    let url = APP_URL;
    try {
      const r = await cpost("/client/app-login", {});
      if (r?.ok && r.url) url = r.url;
    } catch { /* fall back to the plain app URL */ }
    window.location.href = url;
  };
  const downloadInvoice = async () => {
    if (!orderId) { setDl("no-order"); return; }
    setDl("loading");
    try {
      const res = await clientHttp.get(`/client/orders/${orderId}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `bloomconnect-invoice-${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setDl("done");
    } catch { setDl("error"); }
  };

  const cards = [
    { key: "dashboard", icon: LayoutDashboard, title: "Payment Portal", desc: "connect.bybloomlabs.com/dashboard — manage billing, invoices, credits and your plan.",
      action: () => nav("/dashboard"), cta: "Open Payment Portal", testid: "welcome-choice-dashboard" },
    { key: "app", icon: Rocket, title: "The BloomCONNECT App", desc: "app.connect.bybloomlabs.com — you'll be signed straight in, no second password needed.",
      action: launchApp, cta: launching ? "Signing you in\u2026" : "Launch the app", testid: "welcome-choice-app" },
    { key: "invoice", icon: FileText, title: "View / save your invoice", desc: "Download a branded PDF receipt for your records.",
      action: downloadInvoice, cta: dl === "loading" ? "Preparing…" : "Download invoice", testid: "welcome-choice-invoice" },
  ];

  return (
    <div className="bloom-fade w-full max-w-4xl" data-testid="welcome-choices">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-brand-500 text-sm"><CheckCircle2 className="h-5 w-5" /> You're all set</div>
        <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-white">{firstName ? `Welcome aboard, ${firstName}` : "Where would you like to go?"}</h2>
        <p className="mt-3 text-sm text-white/50 max-w-2xl mx-auto">Two places to know: your <b className="text-white/80">Payment Portal</b> (account &amp; billing) and <b className="text-white/80">the App</b> (where you build &amp; run your AI agent).</p>
        {(summary || credits != null || email || paid != null) && (
          <p className="mt-3 text-sm text-white/50" data-testid="welcome-summary">
            {(summary || paid != null) ? <>{summary?.label || "BloomCONNECT"} · <span className="text-white/80 font-medium">{peso(paid || 0)}</span> paid</> : "Payment confirmed"}
            {credits != null ? <> · <span className="text-brand-500 font-medium">{credits} credits ready</span></> : null}
            {email ? <> · {email}</> : null}
          </p>
        )}
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 flex flex-col hover:border-brand-500/40 hover:bg-white/[0.06] transition-colors" data-testid={c.testid}>
              <div className="h-11 w-11 rounded-xl bg-brand-500/15 grid place-items-center text-brand-500"><Icon className="h-5 w-5" /></div>
              <h3 className="mt-5 text-lg font-medium leading-snug text-white">{c.title}</h3>
              <p className="mt-2 text-sm text-white/55 flex-1">{c.desc}</p>
              <button onClick={c.action} disabled={(c.key === "invoice" && dl === "loading") || (c.key === "app" && launching)}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 group-hover:border-brand-500/60 py-2.5 text-sm font-medium transition-colors"
                data-testid={`${c.testid}-btn`}>
                {c.cta} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
      {dl === "no-order" && <p className="mt-4 text-center text-xs text-signal">Invoice will be available in your dashboard shortly.</p>}
      {dl === "error" && <p className="mt-4 text-center text-xs text-negative">Couldn't download the invoice — you can grab it from your dashboard.</p>}
    </div>
  );
}
